import { z } from "zod";
import type { SearchHistoryItem } from "../../../types/GlobalContext";
import type { SearchResultItem } from "../../../types/geocode-ui.types";
import { definePersistedSlice } from "../runtime/definePersistedSlice";

/** Permissive item schema: strips invalid rows in the array transform. */
const SearchHistoryItemSchema = z.object( {
    "result":       z.string(),
    "usageCount":   z.number().default( 1 ),
    "lastUsedDate": z.number().default( () => Date.now() ),
} ).catchall( z.unknown() );

/**
 * Returns whether a history row has a usable display label.
 *
 * @param historyItem Candidate row before cast to {@link SearchHistoryItem}.
 * @returns `true` when `result` is a non-empty string.
 */
const isValidSearchHistoryItem = ( historyItem: unknown ): boolean => {
    return historyItem != null &&
      typeof ( historyItem as SearchHistoryItem ).result === "string" &&
      ( historyItem as SearchHistoryItem ).result.trim() !== "";
};

/**
 * Drops corrupt history rows on read so a single bad entry cannot break the slice.
 *
 * @param items Raw array from storage.
 * @returns Sanitized history list.
 */
const sanitizeSearchHistory = ( items: unknown[] ): SearchHistoryItem[] => {
    return items.filter( isValidSearchHistoryItem ) as SearchHistoryItem[];
};

/**
 * Ordered search history with invalid entries removed on read.
 *
 * @remarks Stored in IndexedDB because the list can grow beyond Preferences size limits.
 */
const SearchHistorySchema = z.array( SearchHistoryItemSchema ).
    default( [] ).
    transform( sanitizeSearchHistory );

/** Array of recent search results with usage metadata. */
export type SearchHistoryState = SearchHistoryItem[];

/** Empty history used as default and migration baseline. */
export const SEARCH_HISTORY_DEFAULT: SearchHistoryState = [];

/** Persisted slice: geocoding search history (lazy, IndexedDB). */
export const searchHistorySlice = definePersistedSlice<SearchHistoryState>( {
    "key":            "searchHistory",
    "currentVersion": 1,
    "storage":        "idb",
    "schema":         SearchHistorySchema,
    "default":        () => SEARCH_HISTORY_DEFAULT,
    "migrations":     {},
} );

/**
 * Sort comparator: higher `usageCount` first.
 *
 * @param a Left item.
 * @param b Right item.
 * @returns Sort order for `Array.prototype.sort`.
 */
const compareSearchHistoryByUsage = ( a: SearchHistoryItem, b: SearchHistoryItem ): number => {
    return b.usageCount - a.usageCount;
};

/**
 * Appends or bumps a search history item (same behavior as legacy StoredContext).
 *
 * @param previous Current search history.
 * @param item New search result item.
 * @returns Updated history sorted by descending `usageCount`.
 */
export const appendSearchHistoryItem = (
    previous: SearchHistoryState,
    item: SearchResultItem,
): SearchHistoryState => {
    const validHistory = previous.filter( isValidSearchHistoryItem );

    const existingItemIndex = validHistory.findIndex( ( historyItem ) => historyItem.result === item.result );

    let newSearchHistory = [ ...validHistory ];
    if ( existingItemIndex === -1 ) {
        const inserted: SearchHistoryItem = {
            ...item,
            "usageCount":   1,
            "lastUsedDate": Date.now(),
        };
        newSearchHistory.unshift( inserted );
    } else {
        const updatedItem: SearchHistoryItem = {
            ...newSearchHistory[existingItemIndex],
            "usageCount":   newSearchHistory[existingItemIndex].usageCount + 1,
            "lastUsedDate": Date.now(),
        };

        // Move bumped item to front so recent searches surface first before sort.
        newSearchHistory.splice( existingItemIndex, 1 );
        newSearchHistory = [ updatedItem, ...newSearchHistory ];
    }

    newSearchHistory.sort( compareSearchHistoryByUsage );

    return newSearchHistory;
};
